import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  type WebhookEndpointRow,
  type WebhookDeliveryRow,
} from "@/lib/webhooks.functions";

export const Route = createFileRoute("/admin/webhooks/")({
  head: () => ({
    meta: [
      { title: "Webhooks & Exports — Optimize Plus" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { checkAdminUnlocked } = await import("@/lib/admin-gate.functions");
    await checkAdminUnlocked();
  },
  component: WebhooksAdmin,
});

const EVENTS = [
  { id: "lead.created", label: "Lead submitted" },
  { id: "conversion.recorded", label: "Conversion event" },
] as const;

const EXPORTS = [
  { type: "leads", label: "Leads" },
  { type: "conversions", label: "Conversion events" },
] as const;

function WebhooksAdmin() {
  const qc = useQueryClient();
  const listFn = useServerFn(listWebhooks);
  const createFn = useServerFn(createWebhook);
  const updateFn = useServerFn(updateWebhook);
  const deleteFn = useServerFn(deleteWebhook);
  const testFn = useServerFn(testWebhook);

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["lead.created"]);
  const [exportDays, setExportDays] = useState(30);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["webhooks"],
    queryFn: () => listFn(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["webhooks"] });

  const create = useMutation({
    mutationFn: () => createFn({ data: { name, url, events: events as never } }),
    onSuccess: () => {
      setName("");
      setUrl("");
      toast.success("Destination added — copy its signing secret below.");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Could not add that destination."),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; active?: boolean; events?: string[] }) =>
      updateFn({ data: vars as never }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Destination removed.");
      invalidate();
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => testFn({ data: { id } }),
    onSuccess: (r: { attempted: number; delivered: number }) => {
      if (r.delivered > 0) toast.success("Test delivered successfully.");
      else toast.error("Test failed — check the status column for the response.");
      invalidate();
    },
  });

  const toggleEvent = (id: string) =>
    setEvents((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const endpoints: WebhookEndpointRow[] = data?.endpoints ?? [];
  const deliveries: WebhookDeliveryRow[] = data?.deliveries ?? [];
  const endpointName = (id: string | null) =>
    endpoints.find((e) => e.id === id)?.name ?? "—";

  return (
    <div className="container-page space-y-10 py-16">
      <header className="space-y-2">
        <h1 className="font-display text-4xl">Webhooks &amp; exports</h1>
        <p className="max-w-2xl text-muted-foreground">
          Forward every lead and conversion event to your CRM, Slack, Zapier, or Make in real
          time — or download the raw data as CSV.
        </p>
      </header>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>CSV export</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Label htmlFor="export-days">Date range (days)</Label>
              <Input
                id="export-days"
                type="number"
                min={1}
                max={730}
                value={exportDays}
                onChange={(e) => setExportDays(Number(e.target.value) || 30)}
              />
            </div>
            {EXPORTS.map((x) => (
              <Button key={x.type} asChild variant="secondary">
                <a href={`/admin/export.csv?type=${x.type}&days=${exportDays}`} download>
                  Download {x.label} CSV
                </a>
              </Button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Exports are UTF-8 with a byte-order mark, so Excel and Google Sheets open them
            correctly. Up to 50,000 rows per download.
          </p>
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Add a destination</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 md:grid-cols-[1fr_2fr_auto] md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (!events.length) {
                toast.error("Pick at least one event to forward.");
                return;
              }
              create.mutate();
            }}
          >
            <div>
              <Label htmlFor="wh-name">Name</Label>
              <Input
                id="wh-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="HubSpot / Zapier"
                required
                maxLength={120}
              />
            </div>
            <div>
              <Label htmlFor="wh-url">Endpoint URL (https)</Label>
              <Input
                id="wh-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                required
                pattern="https://.*"
              />
            </div>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Adding…" : "Add destination"}
            </Button>
            <fieldset className="md:col-span-3">
              <legend className="mb-2 text-sm font-medium">Events to forward</legend>
              <div className="flex flex-wrap gap-4">
                {EVENTS.map((ev) => (
                  <label key={ev.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={events.includes(ev.id)}
                      onChange={() => toggleEvent(ev.id)}
                    />
                    {ev.label} <code className="text-xs text-muted-foreground">{ev.id}</code>
                  </label>
                ))}
              </div>
            </fieldset>
          </form>
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Destinations</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : endpoints.length === 0 ? (
            <p className="text-muted-foreground">No destinations configured yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead>Signing secret</TableHead>
                  <TableHead>Last status</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map((ep) => (
                  <TableRow key={ep.id}>
                    <TableCell>
                      <div className="font-medium">{ep.name}</div>
                      <div className="max-w-[22rem] truncate text-xs text-muted-foreground">
                        {ep.url}
                      </div>
                    </TableCell>
                    <TableCell className="space-x-1">
                      {ep.events.map((e) => (
                        <Badge key={e} variant="secondary">
                          {e}
                        </Badge>
                      ))}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs">
                          {revealed[ep.id] ? ep.secret : "whsec_••••••••"}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setRevealed((r) => ({ ...r, [ep.id]: !r[ep.id] }))
                          }
                        >
                          {revealed[ep.id] ? "Hide" : "Show"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            void navigator.clipboard.writeText(ep.secret);
                            toast.success("Secret copied.");
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {ep.last_attempt_at ? (
                        <span className={ep.last_status && ep.last_status < 400 ? "" : "text-destructive"}>
                          {ep.last_status ?? "error"}
                          {ep.consecutive_failures > 0
                            ? ` · ${ep.consecutive_failures} fail${ep.consecutive_failures > 1 ? "s" : ""}`
                            : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Never sent</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={ep.active}
                        aria-label={`Toggle ${ep.name}`}
                        onCheckedChange={(active) => update.mutate({ id: ep.id, active })}
                      />
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={test.isPending}
                        onClick={() => test.mutate(ep.id)}
                      >
                        Send test
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove.mutate(ep.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Recent deliveries</CardTitle>
        </CardHeader>
        <CardContent>
          {deliveries.length === 0 ? (
            <p className="text-muted-foreground">No deliveries yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {new Date(d.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{endpointName(d.endpoint_id)}</TableCell>
                    <TableCell>
                      <code className="text-xs">{d.event}</code>
                    </TableCell>
                    <TableCell className={d.ok ? "" : "text-destructive"}>
                      {d.ok ? `OK ${d.status ?? ""}` : `Failed ${d.status ?? ""} ${d.error ?? ""}`.trim()}
                    </TableCell>
                    <TableCell>{d.duration_ms ?? "—"} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Verifying a delivery</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>Each request is a JSON POST carrying these headers:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code>X-OptimizePlus-Event</code> — the event name
            </li>
            <li>
              <code>X-OptimizePlus-Timestamp</code> — unix seconds
            </li>
            <li>
              <code>X-OptimizePlus-Signature</code> — <code>sha256=</code> followed by the hex
              HMAC-SHA256 of <code>{"`${timestamp}.${rawBody}`"}</code> using the destination's
              signing secret
            </li>
          </ul>
          <p>
            Reject any request whose recomputed signature doesn't match, or whose timestamp is
            more than five minutes old.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
